import styles from "./TermInfoRollup.module.scss";
import { clsx } from "clsx";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import MiscellaneousTag from "../../../../common/icons/MiscellaneousTag.tsx";
import MiscellaneousCheckmarkCircleFill from "../../../../common/icons/MiscellaneousCheckmarkCircleFill.tsx";
import useProgramName from "./useProgramName.tsx";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import MiscellaneousCheckmarkCircle from "../../../../common/icons/MiscellaneousCheckmarkCircle.tsx";
import useTermStoreMetrics from "./useTermStoreMetrics.tsx";
import useVirtualStore from "./useVirtualStore.tsx";
import useProgramDecision from "./useProgramDecision.tsx";
import useInstitutionProgram from "./useInstitutionProgram.tsx";
import MiscellaneousExclamationCircleFill from "../../../../common/icons/MiscellaneousExclamationCircleFill.tsx";
import useSwitchProgram from "./useSwitchProgram.tsx";
import useTermOptDeadline from "./useTermOptDeadline.tsx";
import { ReactNode } from "react";

function ProgramBlock() {
  const intl = useIntl();
  const decision = useProgramDecision();
  const { isEquitableAccessProgram } = useInstitutionProgram();
  const programName = useProgramName();
  const eaProgramName = useProgramName("equitable_access");
  const {
    requiredIncludedCount,
    requiredToPurchaseCount,
    programCost,
    hasOptableItems,
  } = useTermStoreMetrics();
  const { changeProgram } = useSwitchProgram();
  const { optDeadline } = useTermOptDeadline();
  const isEquitableOptedOut =
    isEquitableAccessProgram && decision !== "equitable_access";
  // todo where does EA fixed e.g. 224 come from?
  const cost = decision === "inclusive_access" ? programCost : 224;
  const formatCurrency = (cost: number) =>
    intl.formatNumber(cost, {
      style: "currency",
      currency: "USD",
    });
  // manual date handling to centralise null date handling
  const formatter = {
    cost: <strong>{formatCurrency(cost)}</strong>,
    date: optDeadline
      ? intl.formatDate(new Date(optDeadline), {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
      : "",
  };
  const subCaption =
    !requiredToPurchaseCount || decision === "equitable_access"
      ? intl.formatMessage(
          {
            defaultMessage: "{cost} billed to student account.",
          },
          formatter
        )
      : intl.formatMessage(
          {
            defaultMessage:
              "{cost} billed to student account, opt out until {date}.",
          },
          formatter
        );

  if (decision === "inclusive_access" && !hasOptableItems) {
    return null;
  }

  // null state if both 0
  // if included count 0 show program to emphasize could be part of program
  if (
    !requiredIncludedCount &&
    requiredToPurchaseCount &&
    decision !== "inclusive_access"
  ) {
    return null;
  }

  const Icon =
    isEquitableAccessProgram && decision !== "equitable_access"
      ? MiscellaneousExclamationCircleFill
      : MiscellaneousCheckmarkCircleFill;

  return (
    <div
      className={clsx(styles.block, styles.included)}
      data-testid="program-rollup"
    >
      <div className={styles.mainLine}>
        <div className={styles.number}>{requiredIncludedCount}</div>
        <div
          className={clsx(
            "vst-icon-with-text",
            styles.iconText,
            isEquitableOptedOut ? styles.warning : styles.recommended
          )}
        >
          <Icon />
          <div className={styles.caption}>
            {isEquitableOptedOut ? eaProgramName : programName}
          </div>
        </div>
      </div>
      {subCaption && !isEquitableOptedOut && (
        <div className={styles.subCaption}>{subCaption}</div>
      )}
      {/* todo virtual store EA is post mvp but sketch out UI  */}
      {isEquitableAccessProgram && !isEquitableOptedOut && (
        <BaseLinkOrButton
          className="vst-link vst-link-size-small"
          onClick={() => changeProgram("no_program")}
        >
          <FormattedMessage defaultMessage="Opt Out" />
        </BaseLinkOrButton>
      )}
      {isEquitableAccessProgram && isEquitableOptedOut && (
        <div className={styles.optInMessage}>
          <FormattedMessage
            defaultMessage={"<a>Opt In</a> until {date}"}
            values={{
              ...formatter,
              a: (chunks: ReactNode) => (
                <BaseLinkOrButton
                  className="vst-link vst-link-size-small"
                  onClick={() => changeProgram("equitable_access")}
                >
                  {chunks}
                </BaseLinkOrButton>
              ),
            }}
          />
        </div>
      )}
    </div>
  );
}

function PurchaseBlock() {
  const intl = useIntl();
  const {
    requiredToPurchaseCount,
    alreadyPurchasedCount,
    requiredToPurchaseTotalCost,
    purchaseCurrency,
  } = useTermStoreMetrics();
  const { program } = useInstitutionProgram();

  const cartMessage = intl.formatMessage(
    {
      defaultMessage: "Add {count} to Cart ({cost})",
    },
    {
      count: requiredToPurchaseCount,
      cost: (
        <FormattedNumber
          value={requiredToPurchaseTotalCost}
          style="currency"
          currency={purchaseCurrency}
        />
      ),
    }
  );

  if (!requiredToPurchaseCount) {
    return null;
  }
  // note cart shortcut only EA as in other programs it is part of the banner
  return (
    <div className={styles.block} data-testid="purchase-rollup">
      <div className={styles.mainLine}>
        <div className={styles.number}>{requiredToPurchaseCount}</div>
        <div className={clsx("vst-icon-with-text", styles.iconText)}>
          <MiscellaneousTag />
          <div className={styles.caption}>
            <FormattedMessage defaultMessage={"to Purchase"} />
          </div>
        </div>
      </div>
      {requiredToPurchaseCount > 0 && program === "equitable_access" && (
        <BaseLinkOrButton
          className={clsx(styles.cartLink, "vst-link vst-link-size-small")}
          onClick={() => alert("Not supported yet")}
        >
          {cartMessage}
        </BaseLinkOrButton>
      )}
      {alreadyPurchasedCount > 0 && (
        <div className={styles.purchasedInfo}>
          <MiscellaneousCheckmarkCircle />
          <FormattedMessage
            tagName="div"
            defaultMessage={"{count} Purchased"}
            values={{ count: alreadyPurchasedCount }}
          />
        </div>
      )}
    </div>
  );
}

export default function TermInfoRollup() {
  const { isVirtualStore } = useVirtualStore();
  const { optWindowOpen } = useTermOptDeadline();

  if (!isVirtualStore || !optWindowOpen) {
    return null;
  }
  return (
    <div className={styles.container}>
      <PurchaseBlock />
      <ProgramBlock />
    </div>
  );
}
